import { useCallback } from 'react';
import { useMapsLibrary } from '@vis.gl/react-google-maps';

export type GeocoderUsageEvent = { hasResult: boolean };
export type GeocoderUsageCallback = (event: GeocoderUsageEvent) => void;

export type AdministrativeArea = {
  municipality: string;
  district: string;
};

export type ReverseGeocodeFn = (
  lat: number,
  lng: number
) => Promise<AdministrativeArea>;

function extractAdministrativeArea(
  components: google.maps.GeocoderAddressComponent[]
): AdministrativeArea | null {
  let municipality: string | undefined;
  let district: string | undefined;
  for (const component of components) {
    if (component.types.includes('administrative_area_level_1')) {
      municipality = component.long_name;
    } else if (component.types.includes('administrative_area_level_2')) {
      district = component.long_name;
    }
  }
  if (!municipality || !district) {
    return null;
  }
  return { municipality, district };
}

export const useGeocoder = (
  onUsage?: GeocoderUsageCallback
): [ReverseGeocodeFn] => {
  const geocodingLibrary = useMapsLibrary('geocoding');

  const reverseGeocode = useCallback<ReverseGeocodeFn>(
    async (lat, lng) => {
      if (!geocodingLibrary) {
        throw new Error('Geocoding library is not loaded yet');
      }
      const geocoder = new geocodingLibrary.Geocoder();
      const response = await geocoder.geocode({ location: { lat, lng } });
      const results = response.results ?? [];
      for (const result of results) {
        const area = extractAdministrativeArea(result.address_components);
        if (area) {
          onUsage?.({ hasResult: true });
          return area;
        }
      }
      onUsage?.({ hasResult: false });
      throw new Error(
        `No administrative area found for coordinate (${lat}, ${lng})`
      );
    },
    [geocodingLibrary, onUsage]
  );

  return [reverseGeocode];
};
